import { findWeekIdsInRange, getFirstDate, getLastDate } from "utils/Date";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useCallback, useMemo, useState } from "react";
import { Spin } from "antd";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import { useFetch } from "hooks/useFetch";
import { useClientsListSource, getClientsListPageData } from "hooks/useClientsListSource";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

const getEventColor = (status) => {
  switch (status) {
    case "Un Assigned":
      return "#FBE2CD";
    case "Assigned":
      return "#E2FBCD";
    case 6:
      return "#FFCCCB";
    default:
      return "#ADD8E6";
  }
};

const getEventBorderColor = (status) => {
  switch (status) {
    case "Un Assigned":
      return "#F18D38";
    case 6:
      return "#38CDF1";
    case "Assigned":
      return "#78F138";
    default:
      return "#F18D38";
  }
};

const textStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const FC_PLUGINS = [timeGridPlugin, dayGridPlugin, interactionPlugin];
const FC_HIDDEN_DAYS = [0];
const FC_HEADER_TOOLBAR = {
  left: "prev,next",
  center: "title",
  right: "timeGridWeek,dayGridMonth",
};

const EngagementWeekCalendar = ({ weeksThisMonth }) => {
  const sDate = getFirstDate(moment());
  const eDate = getLastDate(moment());
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const weekList = findWeekIdsInRange([sDate, eDate]);
  const [payload, setPayload] = useState({
    startDate: new Date(sDate),
    endDate: new Date(eDate),
    employeeIds: [],
    engagementIds: [],
    weekId: weekList,
    managingOffice: currentUser?.managingoffice?.[0]
  });

  const { key: resourceKey, url: resourceUrl } =
    queryKeys.getResourePlanningList(payload?.weekId?.join(","));

  const {
    data: resourceDataRes,
    isLoading: isResourceLoading,
    refetch,
  } = useFetch(
    resourceKey,
    resourceUrl,
    {
      enabled: payload?.weekId?.length > 0,
    },
    payload
  );

  const myBookedDates = useMemo(() => {
    const myResourceData = resourceDataRes?.response?.response?.filter((res) => {
      return res.userId === userInfo.userid;
    });
    return myResourceData?.reduce((acc, res) => {
      const startDate = moment(res.actualBookedStartDate, "YYYY-MM-DD");
      const endDate = moment(res.actualBookedEndDate, "YYYY-MM-DD");

      const dates = [];
      let current = moment(startDate);
      while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
        dates.push({
          date: current.format("YYYY-MM-DD"),
          ...res,
        });
        current.add(1, "day");
      }
      return [...acc, ...dates];
    }, []);
  }, [resourceDataRes, userInfo.userid]);
  
  const events = useMemo(() => {
    const eventsByDate = {};
  
    myBookedDates?.forEach((event) => {
      const startDate = moment(event.bookedStartDate).tz(moment.tz.guess());
      const endDate = moment(event.bookedEndDate).tz(moment.tz.guess());
  
      // Create events for each day between start and end dates
      for (
        let date = startDate.clone();
        date.isBefore(endDate.clone().add(1, "days"));
        date.add(1, "days")
      ) {
        const dateKey = date.format('YYYY-MM-DD');
  
        if (!eventsByDate[dateKey]) {
          eventsByDate[dateKey] = [];
        }
  
        eventsByDate[dateKey].push(event);
      }
    });
  
    const dateRange = [];
  
    // Distribute events across available slots
    Object.entries(eventsByDate).forEach(([date, eventsForDate]) => {
      if (eventsForDate.length === 1) {
        // Only one event, set time from 8:30 AM to 5:30 PM
        const event = eventsForDate[0];
        const start = moment(date).set({ hour: 8, minute: 30 });
        const end = moment(date).set({ hour: 17, minute: 30 });
  
        dateRange.push({
          title: `${event.employeeName}`,
          start: start.toISOString(),
          end: end.toISOString(),
          status: 4,
          ...event,
        });
      } else {
        // More than one event, distribute into 4-hour slots
        const baseStartHour = 8.5; // Start at 8:30 AM
        const timeIncrement = 4; // Each event gets a 4-hour slot
  
        eventsForDate.forEach((event, index) => {
          const startHour = baseStartHour + index * timeIncrement; // Increment start time by 4 hours for each event
          const start = moment(date).set({ hour: Math.floor(startHour), minute: (startHour % 1) * 60 });
          const end = start.clone().add(4, 'hours'); // Each event lasts for 4 hours
  
          dateRange.push({
            title: `${event.employeeName}`,
            start: start.toISOString(),
            end: end.toISOString(),
            status: 4,
            ...event,
          });
        });
      }
    });
  
    return dateRange;
  }, [myBookedDates]);
  
  const { key, url } = useClientsListSource();

  const {
    data: clientsDataRes,
    fetchNextPage,
    isLoading: loading,
    hasNextPage,
  } = useScrollFetch(key, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
  });

  const clients = useMemo(() => {
    return clientsDataRes?.pages?.reduce((acc, page) => {
      const pageData = getClientsListPageData(page);
      if (pageData === undefined) return acc;
      return [...acc, ...pageData];
    }, []);
  }, [clientsDataRes]);

  const clientOptions = useMemo(
    () =>
      clients
        ?.filter((client) => client?.bdo_id !== "NULL")
        ?.map((client) => ({
          label: client?.registeredName,
          value: client?.clientId,
        })),
    [clients]
  );

  const handleDatesSet = useCallback((arg) => {
    const startOfWeek = arg.start; // Start of the week (Date object)
    const endOfWeek = arg.end; // End of the week (Date object)
    const sDate = getFirstDate(moment(arg.start));
    const eDate = getLastDate(moment(arg.end));

    const weeks = findWeekIdsInRange([sDate, eDate]);

    // You can format these dates as needed
    const formattedStart = startOfWeek.toISOString().split("T")[0];
    const formattedEnd = endOfWeek.toISOString().split("T")[0];

    setPayload({
      startDate: formattedStart,
      endDate: formattedEnd,
      employeeIds: [],
      engagementIds: [],
      weekId: weeks,
      managingOffice: currentUser?.managingoffice?.[0]
    });

  }, [currentUser]);

  const renderEventContent = useCallback((info) => {
    const backgroundColor = getEventColor(info.event.extendedProps.status);
    const borderColor = getEventBorderColor(info.event.extendedProps.status);

    const startTime = info.event.start
      ? moment(info.event.start).format("hh:mm A")
      : "N/A";
    const endTime = info.event.end
      ? moment(info.event.end).format("hh:mm A")
      : "N/A";

    const clientName = clientOptions?.find(
      (client) => client?.value === info.event.extendedProps?.clientId
    )?.label;

    return (
      <div
        style={{
          padding: "5px",
          background: backgroundColor,
          border: `1px solid ${borderColor}`,
          color: "#000",
          height: "auto",
          minWidth: "auto",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span style={textStyle}>{info?.event?.title !== "undefined" ? info?.event?.title : ""}</span>
        <span style={textStyle}>Engagement:</span>
        <span style={textStyle}>{info.event.extendedProps?.engagementName ?? "-"}</span>
        <span style={textStyle}>Client:</span>
        <span style={textStyle}>{clientName ?? "-"}</span>
        <p style={textStyle}>
          {startTime} - {endTime}
        </p>
      </div>
    );
  }, [clientOptions]);

  return (
    <>
      <Spin spinning={isResourceLoading}>
        <FullCalendar
          plugins={FC_PLUGINS}
          height="auto"
          contentHeight="auto"
          initialView="timeGridWeek"
          events={events}
          datesSet={handleDatesSet}
          headerToolbar={FC_HEADER_TOOLBAR}
          editable={false}
          selectable={true}
          allDaySlot={false}
          eventOverlap={false}
          slotEventOverlap={true}
          slotMinTime="08:00:00"
          slotMaxTime="18:00:00"
          hiddenDays={FC_HIDDEN_DAYS}
          eventContent={renderEventContent}
        />
      </Spin>
    </>
  );
};

export default EngagementWeekCalendar;
